import { useEffect, useRef } from 'react';
import { Plus } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { CharacterCounter } from '@/components/common/CharacterCounter';
import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SURVEY_OPTION_TEXT_MAX_LENGTH } from '../../constants/survey.constants';
import type { CreateSurveyFormValues } from '../../types/survey.types';
import { ReorderButtons } from './primitives/ReorderButtons';

interface QuestionOptionsEditorProps {
  questionIndex: number;
}

export function QuestionOptionsEditor({ questionIndex }: QuestionOptionsEditorProps) {
  const { control } = useFormContext<CreateSurveyFormValues>();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `questions.${questionIndex}.options`,
  });

  const focusIndexRef = useRef<number | null>(null);
  const rowRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (focusIndexRef.current === null) return;
    const focusIndex = Math.min(focusIndexRef.current, rowRefs.current.length - 1);
    if (focusIndex >= 0) rowRefs.current[focusIndex]?.focus();
    focusIndexRef.current = null;
  }, [fields.length]);

  function addOption() {
    focusIndexRef.current = fields.length;
    append({ text: '' });
  }

  function moveOption(from: number, to: number) {
    focusIndexRef.current = to;
    move(from, to);
  }

  function removeOption(index: number) {
    focusIndexRef.current = index < fields.length - 1 ? index : index - 1;
    remove(index);
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Options</span>
        <Button type="button" variant="outline" size="sm" onClick={addOption} className="gap-1.5">
          <Plus className="size-3.5" />
          Add Option
        </Button>
      </div>

      {fields.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Add at least 2 options to enable this question.
        </p>
      ) : (
        <ul className="space-y-2">
          {fields.map((field, optionIndex) => (
            <li key={field.id} className="flex items-start gap-2">
              <FormField
                control={control}
                name={`questions.${questionIndex}.options.${optionIndex}.text`}
                render={({ field: textField, fieldState }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input
                        {...textField}
                        ref={(node) => {
                          rowRefs.current[optionIndex] = node;
                          textField.ref(node);
                        }}
                        placeholder={`Option ${optionIndex + 1}`}
                        className="h-10"
                        maxLength={SURVEY_OPTION_TEXT_MAX_LENGTH}
                        aria-required="true"
                        aria-invalid={fieldState.invalid || undefined}
                      />
                    </FormControl>
                    <div className="flex items-center justify-between gap-2">
                      <FormMessage />
                      <CharacterCounter
                        current={textField.value?.length ?? 0}
                        max={SURVEY_OPTION_TEXT_MAX_LENGTH}
                        className="ml-auto"
                      />
                    </div>
                  </FormItem>
                )}
              />
              <ReorderButtons
                index={optionIndex}
                total={fields.length}
                itemLabel="option"
                onMoveUp={() => moveOption(optionIndex, optionIndex - 1)}
                onMoveDown={() => moveOption(optionIndex, optionIndex + 1)}
                onRemove={() => removeOption(optionIndex)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
